/**
 * Centralized Role-Based Access Control (RBAC) definitions for UPSC Admin Panel
 * In accordance with ADMIN_MGMT.MD
 */

export const ADMIN_ROLES = {
  SUPER_ADMIN: "SUPER_ADMIN",
  OPERATIONS: "OPERATIONS",
  SUPPORT: "SUPPORT",
  ACADEMICS: "ACADEMICS",
};

export const PERMISSIONS = {
  // Tests
  TESTS_READ: "tests.read",
  TESTS_CREATE: "tests.create",
  TESTS_UPDATE: "tests.update",
  TESTS_PUBLISH: "tests.publish",

  // Questions
  QUESTIONS_READ: "questions.read",
  QUESTIONS_CREATE: "questions.create",
  QUESTIONS_UPDATE: "questions.update",
  QUESTIONS_REVIEW: "questions.review",

  // Users
  USERS_READ: "users.read",
  USERS_UPDATE: "users.update",
  USERS_ACCESS_MANAGE: "users.access.manage",

  // Analytics
  ANALYTICS_READ: "analytics.read",

  // Mentors
  MENTORS_READ: "mentors.read",
  MENTORS_UPDATE: "mentors.update",

  // Notifications
  NOTIFICATIONS_CREATE: "notifications.create",
  NOTIFICATIONS_SEND: "notifications.send",

  // System & Audit
  ADMIN_MANAGE: "admin.manage",
  AUDIT_READ: "audit.read",
};

/**
 * Role to Permission mappings supporting wildcard matching (e.g. tests.* or *)
 */
export const ROLE_PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: ["*"],
  [ADMIN_ROLES.OPERATIONS]: [
    "analytics.*",
    "mentors.*",
    "notifications.*",
  ],
  [ADMIN_ROLES.SUPPORT]: [
    "users.*",
  ],
  [ADMIN_ROLES.ACADEMICS]: [
    "tests.*",
    "questions.*",
  ],
};

/**
 * Checks if a specific role possesses a required permission
 * @param {string} role - The admin role (e.g. SUPER_ADMIN, ACADEMICS)
 * @param {string} requiredPermission - The permission string (e.g. 'tests.read')
 * @returns {boolean}
 */
export const hasPermission = (role, requiredPermission) => {
  if (!role || !requiredPermission) return false;
  const permissions = ROLE_PERMISSIONS[role] || [];

  return permissions.some((perm) => {
    if (perm === "*") return true;
    if (perm.endsWith(".*")) {
      const prefix = perm.slice(0, -2);
      return requiredPermission.startsWith(prefix + ".");
    }
    return perm === requiredPermission;
  });
};

/**
 * Validates whether a user with given role can access any of the provided permissions
 * @param {string} role
 * @param {string[]} permissionsList
 * @returns {boolean}
 */
export const hasAnyPermission = (role, permissionsList = []) => {
  if (!role) return false;
  if (!permissionsList.length) return true;
  return permissionsList.some((p) => hasPermission(role, p));
};
